import { Array, pipe } from 'effect'
import { isNotNullish } from 'effect/Predicate'

import type { NodePath, ViewQueryRecordNode, ViewQueryRecordNodeWithId } from '../../schemas/query/index.ts'

export type ViewQueryRecordNodeWithParent = ViewQueryRecordNode & {
  parent?: ViewQueryRecordNodeWithParent
}

// recordsは元のnodeのchildrenの配列オブジェクトそのものではないので注意。pushなどしても元のrootには反映されない。
export function getRecordNodesByPath(
  recordNode: ViewQueryRecordNodeWithParent,
  nodePath: NodePath,
): ViewQueryRecordNodeWithParent[] {
  if (nodePath.length === 0) {
    // ありえないはず
    return []
  }

  if (nodePath.length === 1) {
    // 指定しているパスがルートであればそのまま返す
    return [recordNode]
  }

  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const childTableNode = recordNode.children.find((x) => x.nodeName === nodePath[1]!)
  if (childTableNode === undefined) {
    return []
  }
  return pipe(
    childTableNode.children,
    Array.map((childRecordNode) => ({
      ...childRecordNode,
      parent: recordNode,
    })),
    Array.flatMap((childRecordNode) => getRecordNodesByPath(childRecordNode, nodePath.slice(1))),
    Array.filter(isNotNullish),
  )
}

// 各親のattrbiutesに重複がない前提でマージする（主にKPI用）
export function flatRecordNodes(
  recordNode: ViewQueryRecordNode,
  parents: ViewQueryRecordNode[],
): Array<Record<string, unknown>> {
  if (recordNode.children.length === 0) {
    const xs = [...parents, recordNode].map((x) => x.attributes)
    return [xs.reduce<Record<string, unknown>>((accumulator, x) => ({ ...accumulator, ...x }), {})]
  }

  return recordNode.children.flatMap((childTableNode) =>
    childTableNode.children.flatMap((childRecordNode) => flatRecordNodes(childRecordNode, [...parents, recordNode])),
  )
}

export function getRecordNodeParents(recordNode: ViewQueryRecordNodeWithParent): ViewQueryRecordNode[] {
  if (recordNode.parent === undefined) {
    return [recordNode]
  }

  return [recordNode, ...getRecordNodeParents(recordNode.parent)]
}

export function getRowRecordIds(row: ViewQueryRecordNodeWithId): string[] {
  const ids: string[] = []
  const records: ViewQueryRecordNode[] = [row]
  while (records.length > 0) {
    const record = records.shift()
    if (record === undefined) {
      continue
    }
    if (record.id !== undefined) {
      ids.push(record.id)
    }
    for (const table of record.children) {
      for (const record of table.children) {
        records.push(record)
      }
    }
  }
  return ids
}
